import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, Button, IconButton, Avatar, Chip, Tabs, Tab, Accordion, AccordionSummary, AccordionDetails, Skeleton} from '@mui/material'
import {ArrowBack, DateRange, AccessTime, ExpandMore, WarningAmber, CheckCircleOutlined, Edit} from '@mui/icons-material'
import {scheduleApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {useNavigate, Link, useParams} from 'react-router-dom'
import {Activity, ScheduleInformation} from './components'
import {useMobile} from '../../hooks'
import {parseError, getInitials, getColorFromName, ignoreTimezone, formatTime} from '../../utils/helpers'
import {format} from 'date-fns'
import {
    MarkAsComplete,
    DeleteVisits,
    DeleteSchedule,
    CancelVisits,
    RescheduleVisits,
    ClockOut,
    UndoClockIn,
    SendSignatureReminder,
    ChangeCaregiver,
    SkeletonContainer,
    TextSkeleton,
    ChipSkeleton,
    AvatarSkeleton,
    ButtonSkeleton,
} from '../../components'
import {styled} from '@mui/material/styles'

const ScheduleCardSkeleton = () => (
    <SkeletonContainer className="card">
        <div className="card-profile">
            <TextSkeleton width={160} fontSize={18} />
            <div className="card-profile-right">
                <ChipSkeleton width={88} height={32} />
                <Skeleton variant="circular" width={40} height={40} />
            </div>
        </div>
        <br />
        <div className="datetime">
            <div className="row-content">
                <Skeleton variant="circular" width={24} height={24} />
                <TextSkeleton width={120} fontSize={16} />
            </div>
            <TextSkeleton width={4} fontSize={16} />
            <div className="row-content">
                <Skeleton variant="circular" width={24} height={24} />
                <TextSkeleton width={140} fontSize={16} />
            </div>
        </div>
        <br />
        <div className="people-card">
            <AvatarSkeleton size={40} />
            <TextSkeleton width={140} fontSize={16} />
        </div>
        <br />
        <div className="row-bottons">
            {[0, 1, 2, 3].map(index => (
                <ButtonSkeleton key={index} width="auto" sx={{flex: 1, minWidth: 200}} />
            ))}
        </div>
    </SkeletonContainer>
)

export default () => {
    const {setMessageBox, branchId} = useGlobalStates()
    const navigate = useNavigate()
    const {mobile} = useMobile()
    const [openMarkAsComplete, setOpenMarkAsComplete] = useState(false)
    const [openDeleteVisits, setOpenDeleteVisits] = useState(false)
    const [openDeleteSchedule, setOpenDeleteSchedule] = useState(false)
    const [openCancelVisits, setOpenCancelVisits] = useState(false)
    const [openRescheduleVisits, setOpenRescheduleVisits] = useState(false)
    const [openClockOut, setOpenClockOut] = useState(false)
    const [openUndoClockIn, setOpenUndoClockIn] = useState(false)
    const [openSendSignatureReminder, setOpenSendSignatureReminder] = useState(false)
    const [openChangeCaregiver, setOpenChangeCaregiver] = useState(false)
    const [tab, setTab] = useState(0)
    const [visit, setVisit] = useState(null)
    const [skeleton, setSkeleton] = useState(true)
    const {scheduleId, visitId} = useParams()
    const [scheduleAddress, setScheduleAddress] = useState(null)
    const [scheduleAddressLongitude, setScheduleAddressLongitude] = useState(0)
    const [scheduleAddressLatitude, setScheduleAddressLatitude] = useState(0)

    const LocalAccordion = styled(Accordion)(({theme}) => ({
        flex: 1,
        minWidth: 300,
        padding: 0,
        borderRadius: 8,
        marginBottom: 0,
        background: '#ffffff',
        border: 'none',
        boxShadow: '0px 0px 8px 0px #00000008',
        '&:before': {display: 'none'},
        '&.Mui-expanded': {
            margin: 0,
        },
    }))

    const LocalAccordionSummary = styled(AccordionSummary)(({theme}) => ({
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 0,
        paddingBottom: 0,
        backgroundColor: '#ffffff',
        '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: '0px 0px',
        },
    }))

    const LocalAccordionDetails = styled(AccordionDetails)(({theme}) => ({
        marginTop: 0,
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 0,
        paddingBottom: 16,
    }))

    useEffect(() => {
        if (branchId) {
            setSkeleton(true)
            scheduleApi
                .getScheduleVisit(branchId, scheduleId, visitId)
                .then(({data}) => {
                    setSkeleton(false)
                    setVisit(data)
                    const address = `${data?.visit_location_summary?.scheduled_address?.street_line1}${data?.visit_location_summary?.scheduled_address?.street_line2 ? ', ' + data?.visit_location_summary?.scheduled_address?.street_line2 : ''}, ${
                        data?.visit_location_summary?.scheduled_address?.city
                    }, ${data?.visit_location_summary?.scheduled_address?.state}, ${data?.visit_location_summary?.scheduled_address?.zip_code}`
                    setScheduleAddress(address)
                    getCoordinates(address)
                })
                .catch(error => {
                    setSkeleton(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })
        }
    }, [branchId])

    const getCoordinates = async address => {
        try {
            const encoded = encodeURIComponent(address)
            const res = await fetch(`https://photon.komoot.io/api/?q=${encoded}&limit=1`)
            const data = await res.json()
            if (data.features.length > 0) {
                const [lon, lat] = data.features[0].geometry.coordinates
                setScheduleAddressLatitude(parseFloat(lat))
                setScheduleAddressLongitude(parseFloat(lon))
            }
        } catch {}
    }

    const loadData = () => {}

    const changeCaregiver = () => {
        setOpenChangeCaregiver(true)
    }

    const markAsComplete = () => {
        setOpenMarkAsComplete(true)
    }

    const cancelSchedule = () => {
        setOpenCancelVisits(true)
    }

    const deleteVisit = () => {
        setOpenDeleteVisits(true)
    }

    const deleteSchedule = () => {
        setOpenDeleteSchedule(true)
    }

    const recordClockOut = () => {
        setOpenClockOut(true)
    }

    const undoClockIn = () => {
        setOpenUndoClockIn(true)
    }

    const sendSignReminderToCaregiver = () => {
        setOpenSendSignatureReminder(true)
    }

    const resetCaregiverSignature = () => {
        // menuClose()
    }

    const updateScheduleReport = () => {
        // menuClose()
    }

    const downloadReport = () => {
        // menuClose()
    }

    const claimBillingToDHS = () => {
        // menuClose()
    }

    const resetSignature = () => {
        // menuClose()
    }

    const reschedule = () => {
        setOpenRescheduleVisits(true)
    }

    const generatePdfReport = () => {
        // menuClose()
    }

    return (
        <>
            {mobile ? (
                <>
                    <div className="header">
                        <Link to="/scheduling">
                            <IconButton>
                                <ArrowBack />
                            </IconButton>
                        </Link>
                        <Typography variant="h6" color="text.primary">
                            Schedule Details
                        </Typography>
                    </div>
                    <br />
                    <br />
                    <br />
                </>
            ) : (
                <>
                    <Typography variant="body1" color="text.secondary">
                        <Link className="link" to="/scheduling">
                            Scheduling
                        </Link>{' '}
                        / Schedule Details
                    </Typography>
                    <br />
                    <div className="back-container">
                        <Link className="back-button" to="/scheduling">
                            <ArrowBack style={{color: '#ffffff'}} fontSize="small" />
                        </Link>
                        <Typography variant="h5" color="text.primary">
                            Schedule Details
                        </Typography>
                    </div>
                    <br />
                    <br />
                </>
            )}
            {skeleton && <ScheduleCardSkeleton />}
            {!skeleton && (
                <div className="card">
                    <div className="card-profile">
                        <Typography variant="h8" color="primary" className="name">
                            {visit?.schedule?.service_agreement_item?.service_name}
                        </Typography>
                        <div className="card-profile-right">
                            {visit?.status === 'scheduled' && (
                                <Chip
                                    label="Scheduled"
                                    variant="filled"
                                    sx={theme => ({
                                        backgroundColor: theme.palette.grey[700],
                                        color: theme.palette.secondary.contrastText,
                                    })}
                                />
                            )}
                            {visit?.status === 'in_progress' && <Chip label="In progress" variant="filled" color="warning" />}
                            {visit?.status === 'ended' && (
                                <Chip
                                    label="Ended"
                                    variant="filled"
                                    sx={theme => ({
                                        backgroundColor: '#02CAD1',
                                        color: theme.palette.primary.contrastText,
                                    })}
                                />
                            )}
                            {visit?.status === 'finished' && <Chip label="Finished" variant="filled" color="info" />}
                            {visit?.status === 'completed' && <Chip label="Completed" variant="filled" color="success" />}
                            {visit?.status === 'cancelled' && <Chip label="Cancelled" variant="filled" color="error" />}
                            <IconButton onClick={() => navigate(`/scheduling/schedule-form/${scheduleId}`)}>
                                <Edit />
                            </IconButton>
                        </div>
                    </div>
                    <br />
                    <div className="datetime">
                        <div className="row-content">
                            <Typography variant="body1" color="text.secondary" style={{marginBottom: -4}}>
                                <DateRange />
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {format(new Date(ignoreTimezone(visit?.date)), 'EEE, dd MMM yyyy')}
                            </Typography>
                        </div>
                        <Typography variant="body1" color="text.secondary">
                            |
                        </Typography>
                        <div className="row-content">
                            <Typography variant="body1" color="text.secondary" style={{marginBottom: -5}}>
                                <AccessTime />
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {formatTime(visit?.start_time)} - {formatTime(visit?.end_time)}
                            </Typography>
                        </div>
                    </div>
                    <br />
                    <div className="people-card">
                        {visit?.schedule?.client?.avatar_url ? (
                            <Avatar src={visit?.schedule?.client?.avatar_url} style={{width: 40, height: 40}} />
                        ) : (
                            <Avatar
                                style={{
                                    backgroundColor: getColorFromName(visit?.schedule?.client?.first_name + ' ' + visit?.schedule?.client?.last_name),
                                    width: 40,
                                    height: 40,
                                    fontSize: 20,
                                }}>
                                {getInitials(visit?.schedule?.client?.first_name + ' ' + visit?.schedule?.client?.last_name)}
                            </Avatar>
                        )}
                        <Typography variant="h8" color="text.primary" className="name">
                            {visit?.schedule?.client?.first_name
                                ? visit?.schedule?.client?.first_name + ' ' + visit?.schedule?.client?.last_name
                                : ''}
                        </Typography>
                    </div>
                    <br />
                    <div className="row-bottons">
                        {visit?.status === 'scheduled' && (
                            <>
                                <Button variant="contained" color="error" onClick={cancelSchedule} className="buttons">
                                    Cancel Schedule
                                </Button>
                                <Button variant="contained" color="error" onClick={deleteVisit} className="buttons">
                                    Delete Visit
                                </Button>
                                <Button variant="outlined" onClick={changeCaregiver} className="buttons">
                                    Change Caregiver
                                </Button>
                                <Button variant="contained" onClick={markAsComplete} className="buttons">
                                    Mark as Complete
                                </Button>
                            </>
                        )}
                        {visit?.status === 'in_progress' && (
                            <>
                                <Button variant="contained" color="error" onClick={cancelSchedule} className="buttons">
                                    Cancel Schedule
                                </Button>
                                <Button variant="outlined" color="error" onClick={undoClockIn} className="buttons">
                                    Undo Clock-In
                                </Button>
                                <Button variant="outlined" onClick={recordClockOut} className="buttons">
                                    Record Clock-Out
                                </Button>
                                <Button variant="contained" onClick={markAsComplete} className="buttons">
                                    Mark as Complete
                                </Button>
                            </>
                        )}
                        {visit?.status === 'ended' && (
                            <>
                                <Button variant="outlined" onClick={sendSignReminderToCaregiver} className="buttons">
                                    Send Sign Reminder to Caregiver
                                </Button>
                                <Button variant="contained" onClick={markAsComplete} className="buttons">
                                    Mark as Complete
                                </Button>
                            </>
                        )}
                        {visit?.status === 'finished' && (
                            <>
                                <Button variant="outlined" onClick={resetCaregiverSignature} className="buttons">
                                    Reset Caregiver Signature
                                </Button>
                                <Button variant="contained" onClick={markAsComplete} className="buttons">
                                    Mark as Complete
                                </Button>
                            </>
                        )}
                        {visit?.status === 'completed' && (
                            <>
                                <Button variant="outlined" onClick={generatePdfReport} className="buttons">
                                    Generate PDF Report
                                </Button>
                                <Button variant="contained" onClick={claimBillingToDHS} className="buttons">
                                    Claim Billing to DHS
                                </Button>
                            </>
                        )}
                        {visit?.status === 'cancelled' && (
                            <>
                                <Button variant="outlined" onClick={reschedule} className="buttons">
                                    Reschedule
                                </Button>
                            </>
                        )}
                        {visit?.schedule_id && (
                            <Button variant="outlined" color="error" onClick={deleteSchedule} className="buttons">
                                Delete Schedule
                            </Button>
                        )}
                    </div>
                </div>
            )}
            {!skeleton && visit?.status !== 'scheduled' && (
                <>
                    <br />
                    <div className="summary-card">
                        <Typography variant="h8" color="text.primary">
                            Summary:
                        </Typography>
                        <br />
                        <br />
                        <div className="collapsed-summary">
                            <LocalAccordion>
                                <LocalAccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1-content" id="panel1-header">
                                    <div className="row-content">
                                        {!visit?.visit_time_summary?.is_late_clock_in && !visit?.visit_time_summary?.is_early_clock_out ? (
                                            <CheckCircleOutlined color="success" />
                                        ) : (
                                            <WarningAmber color="warning" />
                                        )}
                                        <Typography variant="h8" color="text.primary">
                                            Visit Time
                                        </Typography>
                                    </div>
                                </LocalAccordionSummary>
                                <LocalAccordionDetails>
                                    {visit?.visit_time_summary?.is_late_clock_in && (
                                        <>
                                            <Typography variant="body2" color="text.secondary">
                                                Late Clock-In Detected
                                            </Typography>
                                            <br />
                                            <Typography variant="body2" color="error">
                                                The caregiver clocked in {visit?.visit_time_summary?.late_minutes} minutes late.
                                            </Typography>
                                        </>
                                    )}
                                    {visit?.visit_time_summary?.is_early_clock_out && (
                                        <>
                                            <Typography variant="body2" color="text.secondary">
                                                Early Clock-Out Detected
                                            </Typography>
                                            <br />
                                            <Typography variant="body2" color="error">
                                                The caregiver clocked out {visit?.visit_time_summary?.early_minutes} minutes early.
                                            </Typography>
                                        </>
                                    )}
                                    <br />
                                    <div className="visit-content">
                                        <div style={{flex: 1}}>
                                            <Typography variant="h9" color="text.primary">
                                                Schedule Time
                                            </Typography>
                                            <br />
                                            <Typography variant="body2" color="text.primary">
                                                {format(new Date(ignoreTimezone(visit?.date)), 'MMM dd, yyyy')}
                                            </Typography>
                                            <Typography variant="body2" color="text.primary">
                                                {formatTime(visit?.visit_time_summary?.scheduled_start ?? null)} -{' '}
                                                {formatTime(visit?.visit_time_summary?.scheduled_end ?? null)}
                                            </Typography>
                                        </div>
                                        {visit?.clock_in_time && (
                                            <div style={{flex: 1}}>
                                                <Typography variant="h9" color="text.primary">
                                                    Visit Time
                                                </Typography>
                                                <br />
                                                <Typography variant="body2" color="text.primary">
                                                    {format(
                                                        new Date(ignoreTimezone(visit?.visit_time_summary?.actual_start ?? null)),
                                                        'MMM dd, yyyy',
                                                    )}
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {format(new Date(visit?.visit_time_summary?.actual_start ?? null), 'h:mm a')} -{' '}
                                                    {format(new Date(visit?.visit_time_summary?.actual_end ?? null), 'h:mm a')}
                                                </Typography>
                                            </div>
                                        )}
                                    </div>
                                </LocalAccordionDetails>
                            </LocalAccordion>
                            <LocalAccordion>
                                <LocalAccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1-content" id="panel1-header">
                                    <div className="row-content">
                                        {!visit?.visit_location_summary?.clock_in_location_mismatch &&
                                        !visit?.visit_location_summary?.clock_out_location_mismatch ? (
                                            <CheckCircleOutlined color="success" />
                                        ) : (
                                            <WarningAmber color="warning" />
                                        )}
                                        <Typography variant="h8" color="text.primary">
                                            Location
                                        </Typography>
                                    </div>
                                </LocalAccordionSummary>
                                <LocalAccordionDetails>
                                    <Typography variant="body2" color="text.secondary">
                                        Caregiver’s check-in location does not match the client’s registered service address. Manual review is
                                        recommended.
                                    </Typography>
                                    <br />
                                    {visit?.visit_location_summary?.clock_in_location_mismatch ||
                                        (visit?.visit_location_summary?.clock_out_location_mismatch && (
                                            <>
                                                <Typography variant="body2" color="error">
                                                    Your location is far from the client’s address
                                                </Typography>
                                                <br />
                                            </>
                                        ))}
                                    <Typography variant="h9" color="text.primary">
                                        Schedule Location
                                    </Typography>
                                    <br />
                                    <div className="location-map">
                                        <iframe
                                            src={`https://www.google.com/maps?q=${scheduleAddressLatitude},${scheduleAddressLongitude}&hl=es;z=14&output=embed`}
                                            width="137"
                                            height="100"
                                            style={{border: 'none', borderRadius: 16}}
                                        />
                                        <Typography variant="body2" color="text.primary" style={{maxWidth: 150}}>
                                            {scheduleAddress}
                                        </Typography>
                                    </div>
                                    {visit?.clock_in_latitude && visit?.clock_in_longitude && (
                                        <>
                                            <br />
                                            <Typography variant="h9" color="text.primary">
                                                Clock-In Location
                                            </Typography>
                                            <br />
                                            <div className="location-map">
                                                <iframe
                                                    src={`https://www.google.com/maps?q=${visit?.clock_in_latitude},${visit?.clock_in_longitude}&hl=es;z=14&output=embed`}
                                                    width="137"
                                                    height="100"
                                                    style={{border: 'none', borderRadius: 16}}
                                                />
                                                <Typography variant="body2" color="text.primary" style={{maxWidth: 150}}>
                                                    {visit?.visit_location_summary?.clock_in_address}
                                                </Typography>
                                            </div>
                                        </>
                                    )}
                                </LocalAccordionDetails>
                            </LocalAccordion>
                        </div>
                    </div>
                </>
            )}
            <br />
            <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)} allowScrollButtonsMobile variant="fullWidth">
                <Tab label="Schedule Information" />
                <Tab label="Activity" />
            </Tabs>
            <br />
            <br />
            <div style={{display: tab != 0 ? 'none' : 'block'}}>
                <ScheduleInformation visit={visit} skeleton={skeleton} />
            </div>
            <div style={{display: tab != 1 ? 'none' : 'block'}}>
                <Activity visit={visit} />
            </div>
            <MarkAsComplete open={openMarkAsComplete} setOpen={setOpenMarkAsComplete} loadData={loadData} items={[visit]} />
            <DeleteVisits open={openDeleteVisits} setOpen={setOpenDeleteVisits} loadData={loadData} items={[visit]} />
            <DeleteSchedule open={openDeleteSchedule} setOpen={setOpenDeleteSchedule} onDeleted={() => navigate('/scheduling')} item={visit} />
            <CancelVisits open={openCancelVisits} setOpen={setOpenCancelVisits} loadData={loadData} items={[visit]} />
            <RescheduleVisits open={openRescheduleVisits} setOpen={setOpenRescheduleVisits} loadData={loadData} items={[visit]} />
            <ClockOut open={openClockOut} setOpen={setOpenClockOut} loadData={loadData} items={[visit]} />
            <UndoClockIn open={openUndoClockIn} setOpen={setOpenUndoClockIn} loadData={loadData} items={[visit]} />
            <SendSignatureReminder open={openSendSignatureReminder} setOpen={setOpenSendSignatureReminder} loadData={loadData} items={[visit]} />
            <ChangeCaregiver open={openChangeCaregiver} setOpen={setOpenChangeCaregiver} loadData={loadData} items={[visit]} />
        </>
    )
}
